import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { rsvpApi } from "../../../api/rsvp";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { APP_WORKSPACE } from "../../../constants/appWorkspace";
import { queryKeys } from "../../../queryKeys";
import { formatRelativeTime } from "../../../utils/format";
import type { RsvpGuest } from "../../../types/rsvp";
import { isLiveOpsEligible, normalizeGuestNumber } from "../../../types/rsvp";
import "./LiveGuestListPanel.css";

type Props = {
  eventId: string;
  token: string;
};

export const LiveGuestListPanel: React.FC<Props> = ({ eventId, token }) => {
  const queryClient = useQueryClient();
  const workspace = APP_WORKSPACE;
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<RsvpGuest | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Forces a re-render every 30s so "5 min ago" labels stay current without new data.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const rsvpQuery = useQuery<RsvpGuest[]>({
    queryKey: queryKeys.rsvpGuests(workspace, eventId),
    queryFn: () => rsvpApi.listGuests(eventId, token),
    enabled: Boolean(eventId),
  });

  const rows = useMemo(() => {
    // Guest List only: approved Guest-List-channel guests. RSVP and Walk-in
    // guests are admitted through the Tier entry log instead.
    let list: RsvpGuest[] = (rsvpQuery.data ?? []).filter(isLiveOpsEligible);
    list = [...list].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (g) => g.name.toLowerCase().includes(q) || g.phone.toLowerCase().includes(q),
      );
    }
    return list;
  }, [rsvpQuery.data, search]);

  const confirmEntry = async () => {
    if (!pending) return;
    setConfirming(true);
    try {
      // Persist server-side: sets checked_in_at, drops the guest off Live Ops
      // (isLiveOpsEligible) and surfaces it on the RSVP tab's guest list.
      await rsvpApi.checkInGuest(eventId, pending.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.rsvpGuests(workspace, eventId) });
      toast.success(`${pending.name} entered`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to confirm entry.");
    } finally {
      setConfirming(false);
      setPending(null);
    }
  };

  return (
    <div className="eventos-card live-guest-list">
      <div className="eventos-label">Guest List</div>
      <p className="eventos-subtle">
        Confirm entry for approved Guest List guests. RSVP and Walk-in guests are logged in Tier entry log.
      </p>
      <label className="live-guest-list__search">
        <span className="visually-hidden">Search guests</span>
        <input
          className="eventos-input"
          placeholder="Search name or number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>

      <ul className="live-guest-list__rows">
        {rows.length === 0 ? (
          <li className="eventos-empty">No approved Guest List guests yet.</li>
        ) : (
          rows.map((guest) => (
            <li key={guest.id} className="live-guest-list__row">
              <label className="live-guest-list__check">
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => setPending(guest)}
                />
                <span>
                  <strong>{guest.name}</strong>
                  <span className="live-guest-list__meta">
                    {normalizeGuestNumber(guest.phone)} · party {guest.party_size} · Guest List
                    {guest.promoter ? ` · Promoter: ${guest.promoter}` : ""}
                    {` · ${formatRelativeTime(guest.created_at)}`}
                  </span>
                </span>
              </label>
            </li>
          ))
        )}
      </ul>

      <ConfirmDialog
        open={Boolean(pending)}
        title="Confirm Guest Entry"
        message={
          pending ? (
            // The operator verifies all three — name, number, promoter — plus
            // the party size (how many people to let in) before logging entry.
            <dl className="live-guest-list__confirm">
              <dt>Guest Name</dt>
              <dd>{pending.name}</dd>
              <dt>Promoter Name</dt>
              <dd>{pending.promoter || "—"}</dd>
              <dt>Number</dt>
              <dd>{normalizeGuestNumber(pending.phone)}</dd>
              <dt>Party Size</dt>
              <dd>{pending.party_size}</dd>
            </dl>
          ) : null
        }
        confirmLabel={confirming ? "Confirming…" : "Confirm entry"}
        loading={confirming}
        onConfirm={confirmEntry}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};
